import React, { FunctionComponent, useMemo } from 'react';
import { Grid, Box } from '@mui/material';
import { useSafeIntl } from 'bluesquare-components';
import moment from 'moment';

import { useGetEntityTypesDropdown } from '../../hooks/requests';
import MESSAGES from '../messages';
import { Analysis } from '../types';

type Props = {
    analysis: Analysis;
};
type Info = {
    label: string;
    value: string;
};

export const AnalysisTooltipTitle: FunctionComponent<Props> = ({
    analysis,
}) => {
    const { formatMessage } = useSafeIntl();
    const parameters: string[] = useMemo(
        () =>
            Object.entries(analysis.parameters).map(
                ([key, value]) => `${key}: ${value}`,
            ),
        [analysis.parameters],
    );
    const { data: entityTypesDropdown } = useGetEntityTypesDropdown();

    const entytypeName: string = useMemo(() => {
        if (entityTypesDropdown) {
            const type = entityTypesDropdown.find(
                entityType =>
                    entityType.value === parseInt(analysis.entity_type_id, 10),
            );
            return type?.label || '-';
        }
        return '';
    }, [analysis.entity_type_id, entityTypesDropdown]);

    const data: Info[] = useMemo(
        () => [
            {
                label: formatMessage(MESSAGES.algorithm),
                value: analysis.algorithm,
            },
            {
                label: formatMessage(MESSAGES.created_at),
                value: moment(analysis.created_at).format('LTS'),
            },
            {
                label: formatMessage(MESSAGES.finished_at),
                value: analysis.finished_at
                    ? moment(analysis.finished_at).format('LTS')
                    : '-',
            },
            {
                label: formatMessage(MESSAGES.fields),
                value: analysis.fields.join(','),
            },
            {
                label: formatMessage(MESSAGES.parameters),
                value: parameters.length > 0 ? parameters.join(',') : '-',
            },
            {
                label: formatMessage(MESSAGES.entityType),
                value: entytypeName,
            },
        ],
        [analysis, formatMessage, parameters, entytypeName],
    );
    return (
        <Box width={300}>
            <Grid container spacing={1}>
                <Grid item xs={5}>
                    {data.map(item => (
                        <Box
                            key={item.label}
                            justifyContent="flex-end"
                            display="flex"
                        >
                            {item.label}:
                        </Box>
                    ))}
                </Grid>
                <Grid item xs={7}>
                    {data.map((item, index) => (
                        <Box key={`${item.value}-${index}`} display="block">
                            {item.value}
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Box>
    );
};
